import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  TableProps,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { getCommandDetail, getCurrentAwS } from 'api/devices.api';
import { dateForDisplay } from 'libs/dateFormaterPetition';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CommandTrackingStatus,
  Device,
  ResponseQueueAWS,
  StatusCommandType,
} from 'types/devices';

const statusOptions: { label: string; value: StatusCommandType }[] = [
  { label: 'Exitoso', value: 'successful' },
  { label: 'Error', value: 'error' },
  { label: 'Pendiente', value: 'pending' },
];

const renderStatus = (status: CommandTrackingStatus) => {
  if (status === 'successful') {
    return (
      <Tag color="green" title="Exitoso">
        <CheckCircleOutlined /> Exitoso
      </Tag>
    );
  }
  if (status === 'error') {
    return (
      <Tag color="red" title="Error">
        <CloseCircleOutlined /> Error
      </Tag>
    );
  }
  return (
    <Tag color="darkgoldenrod" title="Pendiente">
      <InfoCircleOutlined /> Pendiente
    </Tag>
  );
};

const CommandsList = ({ devices }: { devices: Device[] }) => {
  const [commands, setCommands] = useState<ResponseQueueAWS[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusCommandType>();
  const [serialNumberFilter, setSerialNumberFilter] = useState('');
  const [selectedCommand, setSelectedCommand] =
    useState<ResponseQueueAWS | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const serialNumberOptions = useMemo(
    () =>
      Array.from(
        new Set(
          devices
            .map(
              (device) =>
                device.serialNumber || device.configs?.SerialNumber || ''
            )
            .filter(Boolean)
        )
      )
        .sort((first, second) => first.localeCompare(second))
        .map((serialNumber) => ({
          label: serialNumber,
          value: serialNumber,
        })),
    [devices]
  );

  const updateCommands = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCurrentAwS({
        page: currentPage,
        limit: pageSize,
        status: statusFilter,
        serialNumber: serialNumberFilter || undefined,
      });
      setCommands(response.data.data);
      setTotal(response.data.pagination.total);
      setCurrentPage(response.data.pagination.page);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, serialNumberFilter, statusFilter]);

  useEffect(() => {
    void updateCommands();
  }, [updateCommands]);

  const openCommandDetail = async (command: ResponseQueueAWS) => {
    setSelectedCommand(command);
    setDetailLoading(true);
    try {
      const response = await getCommandDetail(command.cmdId);
      setSelectedCommand((currentCommand) =>
        currentCommand?._id === command._id ? response.data : currentCommand
      );
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns: ColumnsType<ResponseQueueAWS> = [
    {
      title: 'Estado',
      key: 'statusCommands',
      dataIndex: 'status',
      align: 'center',
      width: 110,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
      render: renderStatus,
    },
    {
      title: 'ID',
      key: 'cmdId',
      width: 90,
      dataIndex: 'cmdId',
      render: (cmdId: number, command) => (
        <Button
          type="link"
          onClick={() => void openCommandDetail(command)}
          data-cy={`command-detail-${cmdId}`}
        >
          {cmdId}
        </Button>
      ),
    },
    {
      title: 'No. Serie del Dispositivo',
      key: 'serialNumberCommand',
      dataIndex: 'serialNumber',
      width: 200,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
      align: 'center',
    },
    {
      title: 'Comando',
      key: 'command',
      width: 500,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
      dataIndex: 'command',
      align: 'center',
      render: (command: string) => (
        <Typography.Text ellipsis={{ tooltip: command }}>
          {command}
        </Typography.Text>
      ),
    },
    {
      title: 'Fila',
      key: 'dataCommand',
      width: 200,
      dataIndex: 'queueStatus',
      responsive: ['xl', 'xxl'],
      align: 'center',
      render: (queueStatus) => <>{queueStatus ?? '-'}</>,
    },
    {
      title: 'Última Actualización',
      key: 'updatedAt',
      responsive: ['lg', 'xl', 'xxl'],
      dataIndex: 'updatedAt',
      align: 'center',
      width: 220,
      render: (date) => <>{dateForDisplay(date) ?? '-'}</>,
    },
  ];

  const tableProps: TableProps<ResponseQueueAWS> = {
    bordered: true,
    loading,
    pagination: {
      current: currentPage,
      pageSize,
      total,
      position: ['bottomCenter'],
      showSizeChanger: true,
      pageSizeOptions: [10, 50, 100],
      showTotal: (commandTotal) => `${commandTotal} comandos`,
      onChange: (page, nextPageSize) => {
        if (nextPageSize !== pageSize) {
          setPageSize(nextPageSize);
          setCurrentPage(1);
          return;
        }
        setCurrentPage(page);
      },
    },
    tableLayout: 'fixed',
    size: 'small',
    className: 'table-ps',
  };

  return (
    <>
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <h3 className="sub-title">Comandos</h3>
            </Col>
            <Col>
              <Button
                type="default"
                className="outlined-icon"
                icon={<ReloadOutlined />}
                onClick={() => void updateCommands()}
                title="Actualizar"
                data-cy="refresh-command-list"
              />
            </Col>
          </Row>
        }
        bordered
        size="small"
      >
        <Space wrap className="mb-4">
          <Select<StatusCommandType>
            allowClear
            placeholder="Filtrar por estado"
            options={statusOptions}
            value={statusFilter}
            onChange={(status) => {
              setStatusFilter(status);
              setCurrentPage(1);
            }}
            style={{ minWidth: 190 }}
            data-cy="command-status-filter"
          />
          <Select<string>
            allowClear
            showSearch
            placeholder="Filtrar por número de serie"
            options={serialNumberOptions}
            optionFilterProp="label"
            value={serialNumberFilter || undefined}
            onChange={(serialNumber) => {
              setSerialNumberFilter(serialNumber || '');
              setCurrentPage(1);
            }}
            style={{ width: 300 }}
            data-cy="command-serial-number-filter"
          />
        </Space>
        <Table
          {...tableProps}
          columns={columns}
          dataSource={commands}
          id="commandsTable"
          rowKey="_id"
          data-cy="commands-table"
        />
      </Card>
      <Modal
        title={
          selectedCommand
            ? `Detalle del comando ${selectedCommand.cmdId}`
            : 'Detalle del comando'
        }
        open={selectedCommand !== null}
        onCancel={() => setSelectedCommand(null)}
        footer={null}
        width={760}
        destroyOnHidden
        data-cy="command-detail-modal"
      >
        <Spin spinning={detailLoading}>
          {selectedCommand && (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="ID">
                {selectedCommand.cmdId}
              </Descriptions.Item>
              <Descriptions.Item label="Estado">
                {renderStatus(selectedCommand.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Número de serie">
                {selectedCommand.serialNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo">
                {selectedCommand.commandType || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fila">
                {selectedCommand.queueStatus || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Código de error">
                <span data-cy="command-detail-error-code">
                  {selectedCommand.errorCode || '-'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Creado">
                {dateForDisplay(selectedCommand.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Última actualización">
                {dateForDisplay(selectedCommand.updatedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Comando completo">
                <Typography.Text copyable={{ text: selectedCommand.command }} />
                <pre
                  data-cy="command-detail-full-command"
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {selectedCommand.command}
                </pre>
              </Descriptions.Item>
            </Descriptions>
          )}
        </Spin>
      </Modal>
    </>
  );
};

export default CommandsList;
